import { useState, useEffect, useCallback } from 'react';
import { decodeJwt } from '@src/utils/decode-jwt';
import { authGeneralTokenAPI } from '@src/services/api';

interface AuthResponse {
    firstName: string;
    lastName: string;
    email: string;
    profileImgId?: string;
    image?: string;
    authType: string;
    id: string;
    tel?: string;
    role: 'MEMBER' | 'EVENT_MANAGER' | 'PROJECT_OWNER' | 'ADMIN';
}

interface UseTokenHandlerOptions {
    token: string | null;
    onSuccess?: (response: AuthResponse) => void;
    onError?: (error: Error) => void;
    minTokenLength?: number;
}

interface UseTokenHandlerReturn {
    isProcessing: boolean;
    error: Error | null;
    data: AuthResponse | null;
}

// Utility function for setting secure cookies
const setSecureCookie = (name: string, value: string, expirationDate: Date): void => {
    const secure = window.location.protocol === 'https:' ? '; Secure' : '';
    const sameSite = '; SameSite=Strict';
    document.cookie = `${name}=${value}; path=/; expires=${expirationDate.toUTCString()}${secure}${sameSite}`;
};

// Utility function to validate and decode JWT
const validateAndDecodeToken = (token: string): { exp: number } => {
    try {
        const payload = decodeJwt(token);

        if (!payload.exp || typeof payload.exp !== 'number') {
            throw new Error('Invalid token: missing or invalid expiration');
        }

        // Check if token is already expired
        const currentTime = Math.floor(Date.now() / 1000);
        if (payload.exp < currentTime) {
            throw new Error('Token is expired');
        }

        return payload;
    } catch (error) {
        throw new Error(`Token validation failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
};

export const useTokenHandler = ({
    token,
    onSuccess,
    onError,
    minTokenLength = 100
}: UseTokenHandlerOptions): UseTokenHandlerReturn => {
    const [isProcessing, setIsProcessing] = useState(false);
    const [error, setError] = useState<Error | null>(null);
    const [data, setData] = useState<AuthResponse | null>(null);

    const processToken = useCallback(async (tokenValue: string): Promise<void> => {
        setIsProcessing(true);
        setError(null);

        try {
            // Validate token format and length
            if (tokenValue.length < minTokenLength) {
                throw new Error('Invalid token format');
            }

            // Decode and validate JWT
            const payload = validateAndDecodeToken(tokenValue);

            // Set cookie with expiration
            const expirationDate = new Date(0);
            expirationDate.setUTCSeconds(payload.exp);
            setSecureCookie('zero-carbon-auth.session-token', tokenValue, expirationDate);
            // Call API to validate token and get user data
            const response = await authGeneralTokenAPI(tokenValue);

            if (!response) {
                throw new Error('Invalid response from authentication service');
            }
            console.log('response', response)

            // Validate required fields
            const requiredFields = ['firstName', 'lastName', 'email', 'id', 'role'];
            const missingFields = requiredFields.filter(field => !response[field as keyof AuthResponse]);

            if (missingFields.length > 0) {
                throw new Error(`Missing required fields: ${missingFields.join(', ')}`);
            }

            setData(response);
            onSuccess?.(response);

        } catch (err) {
            const error = err instanceof Error ? err : new Error('Unknown error occurred');
            setError(error);
            onError?.(error);
        } finally {
            setIsProcessing(false);
        }
    }, [onSuccess, onError, minTokenLength]);

    useEffect(() => {
        if (token && token.length >= minTokenLength) {
            processToken(token);
        }
        // eslint-disable-next-line 
    }, [token, minTokenLength]);

    return {
        isProcessing,
        error,
        data
    };
};